import { api } from "./client";
import { getActiveLang } from "../i18n";

export type ReviewService =
  | "TAROT"
  | "PSYCHIC"
  | "MEDIUMSHIP"
  | "DECKS"
  | "SACRED-VISIT"
  | "EVENTS"
  | "PDF"
  | "ONLINE"
  | "IN-PERSON"
  | "SPIRIT-BOX";

export type ServiceFilter = "ALL" | ReviewService;

export type SortKey = "recent" | "oldest" | "highest" | "lowest";

export type Review = {
  id: number;
  name: string;
  service: ReviewService;
  date: string; // ISO yyyy-mm-dd
  rating: number; // 0.5-step, 1..5
  title: string;
  body: string;
  source: string;
  image?: string;
  reply?: { date: string; body: string };
};

export type ReviewSubmission = {
  name: string;
  service: ReviewService;
  title: string;
  body: string;
  rating: number;
  /** Optional uploaded avatar; sent as multipart when present. */
  avatar?: File;
  /** Optional author email; if present they get received/approved emails. */
  email?: string;
};

// Labels are not stored here — they live in the `reviews` translation
// namespace (keyed by `labelKey`) and are resolved via `t()` at the call site
// so the filter pills and the review-form dropdown localize with the UI.
export const SERVICES: { id: ServiceFilter; labelKey: string }[] = [
  { id: "ALL", labelKey: "serviceAll" },
  { id: "TAROT", labelKey: "serviceTarot" },
  { id: "PSYCHIC", labelKey: "servicePsychic" },
  { id: "MEDIUMSHIP", labelKey: "serviceMediumship" },
  { id: "DECKS", labelKey: "serviceDecks" },
  { id: "SACRED-VISIT", labelKey: "serviceSacredVisit" },
  { id: "EVENTS", labelKey: "serviceEvents" },
  { id: "PDF", labelKey: "servicePdf" },
  { id: "ONLINE", labelKey: "serviceOnline" },
  { id: "IN-PERSON", labelKey: "serviceInPerson" },
  { id: "SPIRIT-BOX", labelKey: "serviceSpiritBox" },
];

export async function submitReview(input: ReviewSubmission): Promise<void> {
  const language = getActiveLang();
  if (input.avatar) {
    const form = new FormData();
    form.append("name", input.name);
    form.append("service", input.service);
    form.append("title", input.title);
    form.append("body", input.body);
    form.append("rating", String(input.rating));
    form.append("avatar", input.avatar);
    form.append("language", language);
    if (input.email) form.append("email", input.email);
    // Content-Type undefined lets axios detect FormData and set the boundary,
    // overriding the instance's default application/json.
    await api.post("/api/reviews/", form, {
      headers: { "Content-Type": undefined },
    });
    return;
  }
  await api.post("/api/reviews/", { ...input, language });
}

export async function getReviews(lang: string): Promise<Review[]> {
  const { data } = await api.get<Review[]>("/api/reviews/", {
    params: { lang },
  });
  return data;
}
